import React from 'react'
import {
  Box,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  makeStyles,
} from '@material-ui/core'

const FONT_OPTIONS = [
  { value: 'Cormorant Garamond', category: 'Serif' },
  { value: 'Playfair Display', category: 'Serif' },
  { value: 'Lora', category: 'Serif' },
  { value: 'Merriweather', category: 'Serif' },
  { value: 'EB Garamond', category: 'Serif' },
  { value: 'Libre Baskerville', category: 'Serif' },
  { value: 'Crimson Text', category: 'Serif' },
  { value: 'DM Sans', category: 'Sans-Serif' },
  { value: 'Montserrat', category: 'Sans-Serif' },
  { value: 'Open Sans', category: 'Sans-Serif' },
  { value: 'Roboto', category: 'Sans-Serif' },
  { value: 'Raleway', category: 'Sans-Serif' },
  { value: 'Poppins', category: 'Sans-Serif' },
  { value: 'Inter', category: 'Sans-Serif' },
  { value: 'Nunito', category: 'Sans-Serif' },
  { value: 'Work Sans', category: 'Sans-Serif' },
  { value: 'Oswald', category: 'Sans-Serif' },
  { value: 'Source Sans Pro', category: 'Sans-Serif' },
]

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  colorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  colorSwatch: {
    width: 36,
    height: 36,
    borderRadius: 6,
    border: '2px solid #ddd',
    cursor: 'pointer',
    flexShrink: 0,
    // Hide the default file picker chrome
    overflow: 'hidden',
    position: 'relative',
    '&:hover': {
      borderColor: '#999',
    },
  },
  colorInput: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: 'pointer',
    border: 'none',
    padding: 0,
  },
  colorLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: '#444',
    minWidth: 100,
  },
  hexInput: {
    width: 100,
  },
  section: {
    marginTop: theme.spacing(1),
  },
  fontRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'center',
  },
  previewStrip: {
    display: 'flex',
    gap: 4,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderRadius: 6,
    overflow: 'hidden',
    height: 28,
  },
  previewBlock: {
    flex: 1,
  },
}))

const COLOR_FIELDS = [
  { key: 'colorPrimary', label: 'Primary', description: 'Cover background, headings, table headers' },
  { key: 'colorAccent', label: 'Accent', description: 'Icons, highlights, borders' },
  { key: 'colorGold', label: 'Gold', description: 'Dividers, accent bars' },
  { key: 'colorBackground', label: 'Background', description: 'Section backgrounds' },
  { key: 'colorText', label: 'Text', description: 'Body text color' },
  { key: 'colorMuted', label: 'Muted', description: 'Captions, descriptions' },
]

const ThemeEditor = ({ theme, onChange }) => {
  const classes = useStyles()

  const handleColorChange = (key, value) => {
    onChange({ ...theme, [key]: value })
  }

  const handleFontChange = (key, value) => {
    onChange({ ...theme, [key]: value })
  }

  return (
    <Box className={classes.root}>
      {/* Live color preview strip */}
      <div className={classes.previewStrip}>
        {COLOR_FIELDS.map(f => (
          <div
            key={f.key}
            className={classes.previewBlock}
            style={{ backgroundColor: theme?.[f.key] || '#ccc' }}
            title={f.label}
          />
        ))}
      </div>

      <Typography variant="subtitle2">Colors</Typography>

      {COLOR_FIELDS.map(f => (
        <div key={f.key} className={classes.colorRow}>
          <div
            className={classes.colorSwatch}
            style={{ backgroundColor: theme?.[f.key] || '#ccc' }}
          >
            <input
              type="color"
              className={classes.colorInput}
              value={theme?.[f.key] || '#cccccc'}
              onChange={(e) => handleColorChange(f.key, e.target.value)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div className={classes.colorLabel}>{f.label}</div>
            <div style={{ fontSize: 11, color: '#999' }}>{f.description}</div>
          </div>
          <TextField
            value={theme?.[f.key] || ''}
            onChange={(e) => handleColorChange(f.key, e.target.value)}
            size="small"
            variant="outlined"
            className={classes.hexInput}
            inputProps={{ style: { fontSize: 12, fontFamily: 'monospace', padding: '6px 8px' } }}
          />
        </div>
      ))}

      <Typography variant="subtitle2" className={classes.section}>Fonts</Typography>

      <FormControl variant="outlined" size="small" fullWidth>
        <InputLabel>Display Font</InputLabel>
        <Select
          value={theme?.fontDisplay || 'Cormorant Garamond'}
          onChange={(e) => handleFontChange('fontDisplay', e.target.value)}
          label="Display Font"
          style={{ fontFamily: theme?.fontDisplay || 'inherit' }}
        >
          {FONT_OPTIONS.map(f => (
            <MenuItem key={f.value} value={f.value} style={{ fontFamily: f.value }}>
              {f.value} <span style={{ fontSize: 11, color: '#999', marginLeft: 8 }}>({f.category})</span>
            </MenuItem>
          ))}
        </Select>
        <Typography variant="caption" style={{ color: '#999', marginTop: 2, marginLeft: 14 }}>
          Headlines &amp; titles
        </Typography>
      </FormControl>

      <FormControl variant="outlined" size="small" fullWidth>
        <InputLabel>Body Font</InputLabel>
        <Select
          value={theme?.fontBody || 'DM Sans'}
          onChange={(e) => handleFontChange('fontBody', e.target.value)}
          label="Body Font"
          style={{ fontFamily: theme?.fontBody || 'inherit' }}
        >
          {FONT_OPTIONS.map(f => (
            <MenuItem key={f.value} value={f.value} style={{ fontFamily: f.value }}>
              {f.value} <span style={{ fontSize: 11, color: '#999', marginLeft: 8 }}>({f.category})</span>
            </MenuItem>
          ))}
        </Select>
        <Typography variant="caption" style={{ color: '#999', marginTop: 2, marginLeft: 14 }}>
          Body text &amp; labels
        </Typography>
      </FormControl>

      <Typography variant="subtitle2" className={classes.section}>Logo</Typography>

      <TextField
        label="Logo URL"
        value={theme?.logoUrl || ''}
        onChange={(e) => handleFontChange('logoUrl', e.target.value)}
        size="small"
        variant="outlined"
        fullWidth
        placeholder="https://..."
      />

      <TextField
        label="Logo Width (px)"
        value={theme?.logoWidth || 160}
        onChange={(e) => handleFontChange('logoWidth', parseInt(e.target.value) || 160)}
        size="small"
        variant="outlined"
        type="number"
        style={{ maxWidth: 140 }}
      />
    </Box>
  )
}

export default ThemeEditor
